
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ChevronRight, Home, BookOpen } from "lucide-react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

interface BlogLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  showBreadcrumbs?: boolean;
}

const BlogLayout = ({
  children,
  title = "Blog Preview",
  description = "Preview blog posts and content",
  showBreadcrumbs = true,
}: BlogLayoutProps) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  
  // Animation variants for page transitions
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <>
      <Helmet>
        <title>{title} | Your Company</title>
        <meta name="description" content={description} />
      </Helmet>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        {/* Blog Header */}
        <div className="bg-white border-b border-slate-200 rounded-t-lg">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <Link to="/dashboard/blog" className="text-slate-900 hover:text-slate-700">
                  <h1 className="text-xl font-bold flex items-center">
                    <BookOpen className="mr-2 h-5 w-5" />
                    <span>Blog Preview Mode</span>
                  </h1>
                </Link>
                <p className="text-sm text-slate-600 mt-1">{description}</p>
              </div>
              
              <div className="mt-2 md:mt-0 flex space-x-4">
                <Link 
                  to="/dashboard/blog"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Return to Blog Management
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        {showBreadcrumbs && pathSegments.length > 0 && (
          <div className="bg-white border-b border-slate-200">
            <div className="container mx-auto px-4 py-2">
              <nav className="text-sm">
                <ol className="flex items-center flex-wrap">
                  <li className="flex items-center">
                    <Link 
                      to="/dashboard"
                      className="text-slate-500 hover:text-slate-700 transition-colors flex items-center"
                    >
                      <Home className="h-4 w-4 mr-1" />
                      <span>Dashboard</span>
                    </Link>
                  </li>
                  
                  {pathSegments.map((segment, index) => {
                    // Build the path up to this point
                    const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
                    const isLast = index === pathSegments.length - 1;
                    const formattedSegment = segment
                      .split("-")
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ");
                    
                    return (
                      <li key={path} className="flex items-center">
                        <ChevronRight className="h-4 w-4 mx-2 text-slate-400" />
                        {isLast ? (
                          <span className="font-medium text-slate-900">
                            {formattedSegment}
                          </span>
                        ) : (
                          <Link 
                            to={path}
                            className="text-slate-500 hover:text-slate-700 transition-colors"
                          >
                            {formattedSegment}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        <motion.main
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={{ duration: 0.3 }}
          className="container mx-auto px-4 py-8"
        >
          {children}
        </motion.main>
      </div>
    </>
  );
};

export default BlogLayout;
